import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Loader2, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { API, useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { useLang } from "../i18n/LangContext";
import { toast } from "sonner";

export default function Login() {
  const { lang } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const pl = lang === "pl";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await login(email, password);
      toast.success(pl ? "Zalogowano" : "Logged in");
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `${API}/auth/google/login`;
  };

  return (
    <div className="relative flex min-h-[calc(100vh-72px)] items-center justify-center overflow-hidden px-4 py-12">
      <div className="absolute inset-0 -z-10 opacity-60"
        style={{ backgroundImage: "url(https://static.prod-images.emergentagent.com/jobs/0ab1f86c-1e3c-404e-a589-6a1bdf216739/images/9ee27c012d944cc7d88cb77bc8fc71b54c5211e919cad180a5ceed1967e5498a.png)", backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="absolute inset-0 -z-10 bg-[#0f1117]/85" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#0f1117]/40 via-transparent to-[#0f1117]" />

      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="dc-card w-full max-w-md p-8 sm:p-10 shadow-[0_20px_70px_rgba(253,38,122,0.18)]">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl dc-gradient-bg shadow-[0_12px_40px_rgba(253,38,122,0.4)]">
            <Heart className="h-7 w-7 text-white" fill="white" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-white">
            <span className="dc-gradient-text">{pl ? "Zaloguj się" : "Sign in"}</span>
          </h1>
          <p className="mt-2 text-sm text-[#a8adbd]">{pl ? "Twoje kredyty i raporty zapisane na koncie." : "Your credits and reports saved to your account."}</p>
        </div>

        <button onClick={googleLogin} data-testid="google-login-button"
          className="mb-5 flex w-full items-center justify-center gap-3 rounded-full border border-[#222636] bg-white px-6 py-3.5 font-display font-semibold text-[#0f1117] transition-all hover:-translate-y-0.5">
          <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="h-5 w-5" />
          {pl ? "Kontynuuj z Google" : "Continue with Google"}
        </button>

        <div className="mb-5 flex items-center gap-3 text-xs text-[#a8adbd]">
          <div className="h-px flex-1 bg-[#222636]" /> {pl ? "lub e-mailem" : "or with email"} <div className="h-px flex-1 bg-[#222636]" />
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
          <div>
            <label className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#a8adbd]">
              <Mail className="h-3.5 w-3.5" />E-mail
            </label>
            <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-2xl border border-[#222636] bg-[#0f1117] px-4 py-3.5 text-white outline-none transition-all placeholder:text-[#a8adbd]/40 focus:border-[#ff4458] focus:ring-2 focus:ring-[#ff4458]/30"
              placeholder="ty@example.com" autoFocus />
          </div>
          <div>
            <label className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#a8adbd]">
              <Lock className="h-3.5 w-3.5" />{pl ? "Hasło" : "Password"}
            </label>
            <div className="relative">
              <input data-testid="login-password-input" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-2xl border border-[#222636] bg-[#0f1117] px-4 py-3.5 pr-12 text-white outline-none transition-all placeholder:text-[#a8adbd]/40 focus:border-[#ff4458] focus:ring-2 focus:ring-[#ff4458]/30"
                placeholder="••••••••" />
              <button type="button" onClick={() => setShow((s) => !s)} data-testid="toggle-password-visibility"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a8adbd] hover:text-white" tabIndex={-1}>
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <button type="submit" data-testid="login-submit-button" disabled={loading || !email || !password}
            className="group flex w-full items-center justify-center gap-2 rounded-full dc-gradient-bg px-6 py-3.5 font-display font-semibold text-white shadow-[0_10px_30px_rgba(253,38,122,0.35)] transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" fill="white" />}
            <span>{loading ? "..." : (pl ? "Zaloguj" : "Sign in")}</span>
          </button>
        </form>

        <p className="mt-4 text-center text-xs">
          <Link to="/forgot-password" data-testid="link-to-forgot-password" className="text-[#a8adbd] underline underline-offset-4 hover:text-white">
            {pl ? "Zapomniałeś hasła?" : "Forgot password?"}
          </Link>
        </p>

        <p className="mt-6 text-center text-sm text-[#a8adbd]">
          {pl ? "Nie masz konta?" : "No account?"}{" "}
          <Link to="/register" data-testid="link-to-register" className="font-semibold dc-gradient-text">
            {pl ? "Zarejestruj się" : "Sign up"}
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
